import React, { useState, useEffect } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { useAnalyticsData } from "./hooks/useAnalyticsData";
import DegreeOverview from "./components/DegreeOverview";
import DegreeDetail from "./components/DegreeDetail";
import CourseDetail from "./components/CourseDetail";
import StudentDetail from "./components/StudentDetail";
import CustomText from "../common/general/CustomText/CustomText";
import CustomSpacing from "../common/layout/CustomSpacing/CustomSpacing";
import { useAppConfig } from "../../AppConfigProvider";
import usePagination from "../../hooks/usePagination";
import useSortingDropdown, { SORT_ITEMS } from "../../hooks/useSortingDropdown";
import TableControlsContainer from "../../containers/general/TableControls/TableControlsContainer";
import CustomSearchInput from "../common/general/CustomSearchInput/CustomSearchInput";
import SortingDropdown from "../common/TableControls/SortingDropdown/SortingDropdown";
import { Course, Degree, DegreesBySchoolByQueryData } from "../../graphql/generated/graphql";

const EnhancedAnalyticsDashboard: React.FC = () => {
  const { hasDegrees } = useAppConfig();

  // State for navigation and selection
  const [activeAnalyticsParams, setActiveAnalyticsParams] = useState<{
    entityType: "degree" | "course" | "student";
    entityId: string | null;
    entityGroupName: string;
  }>({
    entityType: hasDegrees ? "degree" : "course",
    entityId: null,
    entityGroupName: hasDegrees ? "Degree Programs" : "Courses",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 10 });
  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "date_desc",
  });

  // Fetch analytics data
  const {
    coursesData,
    studentsData,
    completionData,
    degreesData,
    coursesByDegreeData,
    detailedCourse,
    studentDetailedAnalyticsData,
    setCourseIdForDetailedCourse,
    setDegreeIdForDetailedDegree,
    setStudentIdForDetailedStudent,
    isLoading: isLoadingAnalytics,
    refetchData,
  } = useAnalyticsData({
    searchQuery,
    page,
    pageSize,
    sortBy,
  });

  useEffect(() => {
    refetchData();
  }, [searchQuery, page, pageSize, sortBy]);

  const handleEntitySelect = (
    entityType: "degree" | "course" | "student",
    entityId: string,
    entityGroupName: string
  ) => {
    if (!entityId) {
      setSearchQuery("");
      setPage(1);
    }
    setSortBy("date_desc");
    setSortDropdownOpen(false);
    setActiveAnalyticsParams({ entityType, entityId, entityGroupName });
  };

  // Fetch detailed course when selectedCourseId changes
  useEffect(() => {
    if (activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "course") {
      setCourseIdForDetailedCourse(activeAnalyticsParams.entityId);
    }
    if (activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "degree") {
      setDegreeIdForDetailedDegree(activeAnalyticsParams.entityId);
    }
    if (activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "student") {
      setStudentIdForDetailedStudent(activeAnalyticsParams.entityId);
    }
  }, [activeAnalyticsParams.entityId, activeAnalyticsParams.entityType]);

  // Render content based on active view and selections
  const renderContent = () => {
    return (
      <>
        {activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "student" && (
          <StudentDetail
            studentDetailedAnalyticsData={studentDetailedAnalyticsData}
            onBackToStudents={() => handleEntitySelect("student", "", "Students")}
            loading={isLoadingAnalytics}
          />
        )}
        {activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "course" && (
          <CourseDetail
            courseId={activeAnalyticsParams.entityId}
            onBackToCourses={() => handleEntitySelect("course", "", "Courses")}
            completionData={completionData?.data}
            detailedCourse={detailedCourse}
            loading={isLoadingAnalytics}
          />
        )}
        {activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "degree" && (
          <DegreeDetail
            degreeId={activeAnalyticsParams.entityId}
            onBackToDegrees={() => handleEntitySelect("degree", "", "Degree Programs")}
            onCourseSelect={(courseId) => handleEntitySelect("course", courseId, "Courses")}
            degreesData={degreesData.degrees as Degree[]}
            coursesByDegreeData={coursesByDegreeData?.courses as Course[]}
            loading={isLoadingAnalytics}
          />
        )}
        {!activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "degree" && (
          <DegreeOverview
            degreesData={degreesData as DegreesBySchoolByQueryData}
            onDegreeSelect={(degreeId) => handleEntitySelect("degree", degreeId, "Degree Programs")}
            isLoading={isLoadingAnalytics}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        )}
        {!activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "course" && (
          <View>
            {coursesData?.data?.map((course) => {
              return (
                <TouchableOpacity
                  key={course.id}
                  style={styles.courseCard}
                  onPress={() => handleEntitySelect("course", course.id, "Courses")}>
                  <CustomText
                    text={course.label || "Unnamed Course"}
                    weight="bold"
                    size="m"
                    useTranslationText={false}
                  />
                  <CustomText
                    text={`Success Points: ${course.success_points || 0}`}
                    size="s"
                    useTranslationText={false}
                  />
                  <CustomText
                    text={`Confusion Points: ${course.confusion_points || 0}`}
                    size="s"
                    useTranslationText={false}
                  />
                  <CustomText text={`Enrollment: ${course.enrollment || 0}`} size="s" useTranslationText={false} />
                  <CustomSpacing type="vertical" size="s" />
                  <CustomText
                    text="Tap to view details →"
                    size="xs"
                    style={styles.actionText}
                    useTranslationText={false}
                  />
                </TouchableOpacity>
              );
            })}
            {coursesData?.count > 0 && (
              <TableControlsContainer
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalCount={coursesData?.count}
              />
            )}
          </View>
        )}
        {!activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "student" && (
          <View>
            {studentsData?.data?.map((student) => {
              return (
                <TouchableOpacity
                  key={student.id}
                  style={styles.studentCard}
                  onPress={() => handleEntitySelect("student", student.id, "Students")}>
                  <CustomText
                    text={student.label || "Unnamed Student"}
                    weight="bold"
                    size="m"
                    useTranslationText={false}
                  />
                  <CustomText
                    text={`Success Points: ${student.success_points || 0}`}
                    size="s"
                    useTranslationText={false}
                  />
                  <CustomText
                    text={`Confusion Points: ${student.confusion_points || 0}`}
                    size="s"
                    useTranslationText={false}
                  />
                  <CustomText
                    text={`Questions Asked: ${student.questions_asked || 0}`}
                    size="s"
                    useTranslationText={false}
                  />
                  <CustomSpacing type="vertical" size="s" />
                  <CustomText
                    text="Tap to view details →"
                    size="xs"
                    style={styles.actionText}
                    useTranslationText={false}
                  />
                </TouchableOpacity>
              );
            })}
            {studentsData?.count > 0 && (
              <TableControlsContainer
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalCount={studentsData?.count}
              />
            )}
          </View>
        )}
      </>
    );
  };

  return (
    <View style={styles.container}>
      <CustomSpacing type="vertical" size="m" />

      {/* Hide tabs when a specific item is selected (drill-down mode) */}
      {!activeAnalyticsParams.entityId && (
        <View style={styles.tabContainer}>
          <View style={styles.tabRow}>
            {hasDegrees && (
              <TouchableOpacity
                style={[styles.tab, activeAnalyticsParams.entityType === "degree" && styles.activeTab]}
                onPress={() => handleEntitySelect("degree", "", "Degree Programs")}>
                <CustomText
                  text="Degree Programs"
                  size="m"
                  weight={activeAnalyticsParams.entityType === "degree" ? "bold" : "normal"}
                  useTranslationText={false}
                />
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.tab, activeAnalyticsParams.entityType === "course" && styles.activeTab]}
              onPress={() => handleEntitySelect("course", "", "Courses")}>
              <CustomText
                text="Courses"
                size="m"
                weight={activeAnalyticsParams.entityType === "course" ? "bold" : "normal"}
                useTranslationText={false}
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, activeAnalyticsParams.entityType === "student" && styles.activeTab]}
              onPress={() => handleEntitySelect("student", "", "Students")}>
              <CustomText
                text="Students"
                size="m"
                weight={activeAnalyticsParams.entityType === "student" ? "bold" : "normal"}
                useTranslationText={false}
              />
            </TouchableOpacity>
          </View>
        </View>
      )}

      <CustomSpacing type="vertical" size="l" />

      <View style={styles.tabContent}>
        <View style={styles.tabHeader}>
          <CustomText text={activeAnalyticsParams.entityGroupName} size="xl" weight="bold" useTranslationText={false} />
        </View>

        <CustomSpacing type="vertical" size="m" />

        <View style={styles.controlsContainer}>
          <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          <SortingDropdown
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortDropdownOpen={sortDropdownOpen}
            setSortDropdownOpen={setSortDropdownOpen}
            sortItems={SORT_ITEMS}
            label="Sort by"
          />
        </View>

        <CustomSpacing type="vertical" size="m" />

        {renderContent()}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  tabContainer: {
    marginVertical: 16,
  },
  tabRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e5e5",
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  activeTab: {
    borderBottomColor: "#0084FF",
  },
  tabContent: {
    flex: 1,
  },
  tabHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  courseCard: {
    padding: 16,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#e5e5e5",
  },
  studentCard: {
    padding: 16,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#e5e5e5",
  },
  actionText: {
    color: "#0084FF",
    fontStyle: "italic",
  },
  controlsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    justifyContent: "flex-start",
    width: "100%",
    zIndex: 3000,
  },
});

export default EnhancedAnalyticsDashboard;
